import { ButtonCus } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Textarea } from '@/components/common/Textarea';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface RequestCancelProps {
    orderIds: number[],
    reload?: Function
}
export default observer(function RequestCancel({ orderIds, reload }: RequestCancelProps) {
    // hook
    const { t } = useTranslation();

    // store
    const { reservationStore: { requestCancelReservation }, modalStore: { hideAllModals, showAlertModal } } = useStore();
    // validate schema
    const validateSchema = yup.object().shape({
        title: yup.string().trim().required('form_error_validate.required'),
        content: yup.string().trim().required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            title: '',
            content: ''
        }
    });

    // function
    const onSubmit = async (data: FormData) => {
        const payload = {
            ...data,
            orderIds
        };
        const res = await flowResult(requestCancelReservation(payload));
        if (res) {
            hideAllModals();
            showAlertModal({
                id: 'send-success',
                type: 'success',
                content: t('messages.send_success'),
                onClickXIcon: () => {
                    reload && reload();
                }
            });
        } else {
            showAlertModal({
                id: 'send-error',
                type: 'error',
                content: t('messages.send_failure')
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className='flex flex-col gap-4'>
                    <FormField
                        control={form.control}
                        name='title'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.title')}</FormLabel>
                                <FormControl>
                                    <Input {...field} placeholder={t('placeholder.enter')}></Input>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='content'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.reason')}</FormLabel>
                                <FormControl>
                                    <Textarea {...field} rows={5} maxLength={500} placeholder={t('placeholder.enter')}></Textarea>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div>
                    <div className='mt-8 flex justify-center'>
                        <ButtonCus variant='default' disabled={form.formState.isSubmitting} size='sm' type='submit'>{t('button.send')}</ButtonCus>
                    </div>
                </div>
            </form>
        </Form>
    );
});
